/** Окно исходного листа РД (пункт 9 плана по замечаниям ПТО, решение 15):
 *  имя окна, канал синхронизации, сообщения канала и адрес окна.
 *
 *  Экран «Позиции объёма» открывает лист в отдельном окне браузера с именем
 *  `osnova_pto_rd_sheet` (его можно увести на второй монитор). Следующие клики —
 *  по номеру строки, «Источнику», отметке «✓ л. 6» — не открывают новых вкладок:
 *  экран шлёт `show` в `BroadcastChannel('osnova_pto_sheet_viewer')`, окно
 *  переключает лист и отвечает `shown`. Окно при открытии и на `ping` говорит
 *  `hello`, при закрытии — `bye`: так экран знает, есть ли кому показывать.
 *
 *  Панель правки №8 в окне (выпуск 4): после правки окно шлёт
 *  `pto_volume_changed` с id ревизии — экран «Позиции объёма» этой ревизии
 *  перезапрашивает позиции. В канал идут только id ревизии, проекта и позиции,
 *  лист, рамки и подписи строки — без токенов и данных позиций: их окно берёт
 *  своим запросом к API с проверкой доступа к проекту.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`rdSheet/logic.test.mjs`). */
import type { PtoVolumePosition } from '@shared/api';

/** Имя окна листа: `window.open(url, SHEET_WINDOW_NAME)` не плодит вкладок. */
export const SHEET_WINDOW_NAME = 'osnova_pto_rd_sheet';
/** Канал синхронизации экрана и окна листа. */
export const SHEET_CHANNEL = 'osnova_pto_sheet_viewer';
/** Версия протокола: сообщения другой версии не принимаются. */
export const SHEET_PROTOCOL = 1;
/** Адрес окна листа в приложении (страница без меню ОСНОВЫ). */
export const SHEET_ROUTE = '/agents/pto/rd-sheet';

/** Прямоугольник `[x0, y0, x1, y1]` в пунктах листа без поворота (`raw.box`). */
export type Rect = [number, number, number, number];

/** Что показать в окне: лист ревизии, рамки строки и шапки, подписи. */
export interface SheetTarget {
  revisionId: string;
  /** Лист PDF, с 1. */
  page: number;
  /** Файл ревизии (`raw.file`); null — единственный файл ревизии. */
  file: string | null;
  /** Рамка строки (`raw.box`); null — у строки рамки нет (текстовая таблица до
   *  переобработки, позиция ИИ): лист без подсветки. */
  box: Rect | null;
  /** Рамка — не самой строки, а строки, после которой внесена ручная строка №8
   *  (у ручной строки своей рамки нет). */
  boxIsAnchor: boolean;
  /** Рамка шапки таблицы (`raw.head_box`) на листе `headPage`. */
  headBox: Rect | null;
  headPage: number | null;
  positionId: string | null;
  /** Проект позиции: по нему окно спрашивает права на правки №8 (панель
   *  правки); null — панели нет. */
  projectId: string | null;
  /** «СП01 №5 — Вставка гибкая круглая VGKR». */
  title: string;
  /** «СП01 л. 2 (стр. PDF 56)». */
  place: string;
  /** «7 шт». */
  qty: string;
  /** Шифр комплекта. */
  docCode: string | null;
}

export type SheetMessage =
  | { type: 'show'; v: 1; id: string; target: SheetTarget }
  | { type: 'shown'; v: 1; id: string }
  | { type: 'hello'; v: 1 }
  | { type: 'bye'; v: 1 }
  | { type: 'ping'; v: 1 }
  /** Окно листа: позиции ревизии изменились (правка №8 в панели окна). */
  | { type: 'pto_volume_changed'; v: 1; revisionId: string };

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function str(v: unknown, max = 500): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null;
}

function posInt(v: unknown): number | null {
  const n = typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : null;
}

/** Прямоугольник из `raw.box` / сообщения / адреса: четыре конечных числа,
 *  непустой; иначе null. Строка «x0,y0,x1,y1» тоже принимается. */
export function rectOf(v: unknown): Rect | null {
  const arr = typeof v === 'string' ? v.split(',').map((s) => Number(s.trim())) : v;
  if (!Array.isArray(arr) || arr.length !== 4) return null;
  if (!arr.every((x) => typeof x === 'number' && Number.isFinite(x))) return null;
  const [x0, y0, x1, y1] = arr as number[];
  return x1 > x0 && y1 > y0 ? [x0, y0, x1, y1] : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Поля позиции, по которым строится цель окна (`project_id` — для панели
 *  правки; у старых данных может не быть). */
export type TargetPosition = Pick<PtoVolumePosition, 'id' | 'revision_id' | 'page_number' | 'raw'> &
  Partial<Pick<PtoVolumePosition, 'project_id'>>;

/** Ручная строка №8 («строка не распознана программой», `raw.source =
 *  'manual'`): своей рамки на листе у неё нет. */
export function isManualTarget(p: Pick<TargetPosition, 'raw'>): boolean {
  return asObj(p.raw)?.source === 'manual';
}

function rowIndex(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

/**
 * Строка таблицы, после которой внесена ручная строка №8, — её подсвечивает
 * окно листа. Сначала по `raw.anchor_position_id` (якорь при внесении), затем
 * по месту: тот же лист и файл, та же таблица (`raw.table_index`) и строка
 * таблицы `raw.after_row_index` (после переобработки у якоря новый id). Не
 * ручная строка или якорь не найден — null.
 */
export function manualAnchorOf<T extends TargetPosition>(
  p: TargetPosition,
  positions: Iterable<T>,
): T | null {
  if (!isManualTarget(p)) return null;
  const raw = asObj(p.raw) ?? {};
  const file = str(raw.file);
  const table = raw.table_index;
  const after = rowIndex(raw.after_row_index);
  const anchorId = str(raw.anchor_position_id, 64);
  let byPlace: T | null = null;
  for (const q of positions) {
    if (q.id === p.id || isManualTarget(q) || q.page_number !== p.page_number) continue;
    const qr = asObj(q.raw) ?? {};
    if (str(qr.file) !== file || qr.table_index !== table) continue;
    if (anchorId && q.id === anchorId) return q;
    if (!byPlace && after !== null && rowIndex(qr.row_index) === after) byPlace = q;
  }
  return byPlace;
}

/** Подписи для окна (их готовит экран: номер строки, место, количество). */
export interface TargetLabels {
  title: string;
  place: string;
  qty: string;
  docCode: string | null;
}

/**
 * Цель окна по позиции: лист и файл, рамка строки (`raw.box`), рамка шапки
 * (`raw.head_box`) и её лист (`raw.head_page`, нет — тот же лист). Лист
 * неизвестен — null. `anchor` — строка, после которой внесена ручная строка
 * ([`manualAnchorOf`]): у ручной строки своей рамки нет, подсвечивается якорь
 * (`boxIsAnchor`), шапка — его таблицы.
 */
export function targetOf(
  p: TargetPosition,
  labels: TargetLabels,
  anchor?: TargetPosition | null,
): SheetTarget | null {
  const raw = asObj(p.raw);
  const sheet = asObj(raw?.sheet);
  const page = posInt(p.page_number) ?? posInt(sheet?.page);
  if (!page) return null;
  const own = rectOf(raw?.box);
  const anchorRaw = !own && anchor && anchor.page_number === page ? asObj(anchor.raw) : null;
  const anchorBox = anchorRaw ? rectOf(anchorRaw.box) : null;
  const headSrc = anchorBox && !rectOf(raw?.head_box) ? anchorRaw : raw;
  const headBox = rectOf(headSrc?.head_box);
  const projectId = str(p.project_id, 64);
  return {
    revisionId: p.revision_id,
    page,
    file: str(raw?.file) ?? str(sheet?.file),
    box: own ?? anchorBox,
    boxIsAnchor: !own && !!anchorBox,
    headBox,
    headPage: headBox ? (posInt(headSrc?.head_page) ?? page) : null,
    positionId: p.id,
    projectId: projectId && UUID_RE.test(projectId) ? projectId : null,
    title: labels.title,
    place: labels.place,
    qty: labels.qty,
    docCode: labels.docCode,
  };
}

/** Цель из сообщения или адреса — с проверкой полей; негодная — null. */
export function sanitizeTarget(v: unknown): SheetTarget | null {
  const o = asObj(v);
  const revisionId = str(o?.revisionId, 64);
  const page = posInt(o?.page);
  if (!o || !revisionId || !UUID_RE.test(revisionId) || !page) return null;
  const headBox = rectOf(o.headBox);
  const box = rectOf(o.box);
  const projectId = str(o.projectId, 64);
  return {
    revisionId,
    page,
    file: str(o.file),
    box,
    boxIsAnchor: !!box && (o.boxIsAnchor === true || o.boxIsAnchor === '1'),
    headBox,
    headPage: headBox ? (posInt(o.headPage) ?? page) : null,
    positionId: str(o.positionId, 64),
    projectId: projectId && UUID_RE.test(projectId) ? projectId : null,
    title: str(o.title) ?? '',
    place: str(o.place) ?? '',
    qty: str(o.qty, 100) ?? '',
    docCode: str(o.docCode, 200),
  };
}

/** Сообщение канала: известного вида и нашей версии; иначе null. */
export function parseSheetMessage(data: unknown): SheetMessage | null {
  const o = asObj(data);
  if (!o || o.v !== SHEET_PROTOCOL) return null;
  switch (o.type) {
    case 'show': {
      const id = str(o.id, 64);
      const target = sanitizeTarget(o.target);
      return id && target ? { type: 'show', v: 1, id, target } : null;
    }
    case 'shown': {
      const id = str(o.id, 64);
      return id ? { type: 'shown', v: 1, id } : null;
    }
    case 'hello':
    case 'bye':
    case 'ping':
      return { type: o.type, v: 1 };
    case 'pto_volume_changed': {
      // Только id ревизии: прочие поля сообщения не принимаются.
      const revisionId = str(o.revisionId, 64);
      return revisionId && UUID_RE.test(revisionId)
        ? { type: 'pto_volume_changed', v: 1, revisionId }
        : null;
    }
    default:
      return null;
  }
}

function rectParam(r: Rect | null): string | null {
  return r ? r.map((x) => String(Math.round(x * 10) / 10)).join(',') : null;
}

/** Цель — в параметры адреса окна (перезагрузка окна показывает тот же лист). */
export function targetToSearch(t: SheetTarget): string {
  const q = new URLSearchParams();
  const put = (k: string, v: string | number | null | undefined) => {
    if (v !== null && v !== undefined && v !== '') q.set(k, String(v));
  };
  put('rev', t.revisionId);
  put('page', t.page);
  put('file', t.file);
  put('box', rectParam(t.box));
  if (t.box && t.boxIsAnchor) put('anchor', 1);
  put('hbox', rectParam(t.headBox));
  if (t.headBox && t.headPage !== t.page) put('hpage', t.headPage);
  put('pos', t.positionId);
  put('prj', t.projectId);
  put('title', t.title);
  put('place', t.place);
  put('qty', t.qty);
  put('doc', t.docCode);
  return q.toString();
}

/** Цель из адреса окна (`location.search`); не разобрали — null. */
export function targetFromSearch(search: string): SheetTarget | null {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return sanitizeTarget({
    revisionId: q.get('rev'),
    page: q.get('page'),
    file: q.get('file'),
    box: q.get('box'),
    boxIsAnchor: q.get('anchor'),
    headBox: q.get('hbox'),
    headPage: q.get('hpage'),
    positionId: q.get('pos'),
    projectId: q.get('prj'),
    title: q.get('title'),
    place: q.get('place'),
    qty: q.get('qty'),
    docCode: q.get('doc'),
  });
}

/** Адрес окна листа: `<origin>/agents/pto/rd-sheet?<цель>`. */
export function sheetWindowUrl(origin: string, t: SheetTarget): string {
  return `${origin.replace(/\/+$/, '')}${SHEET_ROUTE}?${targetToSearch(t)}`;
}

/** Строка — на листе-продолжении таблицы: шапка на другом листе (закреплённая
 *  полоска шапки сверху окна, решение 17). */
export function isContinuation(t: Pick<SheetTarget, 'page' | 'headBox' | 'headPage'>): boolean {
  return !!t.headBox && t.headPage !== null && t.headPage !== t.page;
}

/** Лист по умолчанию для панели правки №8 в окне — тот, что открыт: стр. PDF
 *  и «СП01 л. 2» из подписи места («СП01, л. 2 (стр. PDF 56)»). Листа части
 *  в подписи нет — только страница (поле `sheet` не задаётся вовсе, чтобы не
 *  затереть лист позиции). */
export function sheetHintOf(t: Pick<SheetTarget, 'page' | 'place'>): {
  page: string;
  sheet?: string;
} {
  const m = /^(\S+?),? л\. ([^\s(]+)/.exec(t.place.trim());
  return m ? { page: String(t.page), sheet: `${m[1]} л. ${m[2]}` } : { page: String(t.page) };
}

/** Тот же лист той же ревизии и файла. */
export function sameSheet(
  a: Pick<SheetTarget, 'revisionId' | 'page' | 'file'> | null,
  b: Pick<SheetTarget, 'revisionId' | 'page' | 'file'> | null,
): boolean {
  return !!a && !!b && a.revisionId === b.revisionId && a.page === b.page && a.file === b.file;
}
